import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { invoicesApi, paymentsApi } from '../services/api';
import Sidebar from '../components/Sidebar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Euro, 
  FileText,
  Loader2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Search,
  Filter,
  Download,
  Eye,
  X,
  Calendar,
  User,
  Users,
  Receipt,
  TrendingUp,
  CreditCard,
  ChevronRight,
  Home,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Payments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [forecastSummary, setForecastSummary] = useState([]);
  const [loadingForecast, setLoadingForecast] = useState(true);

  // Memoize expensive total to avoid recalculating on every render
  const forecastTotal = useMemo(
    () => forecastSummary.reduce((sum, hk) => sum + (hk.total_amount || 0), 0),
    [forecastSummary]
  );
  
  // Housekeeper details modal state
  const [showHkDetailModal, setShowHkDetailModal] = useState(false);
  const [hkDetails, setHkDetails] = useState(null);
  const [loadingHkDetails, setLoadingHkDetails] = useState(false);
  const [hkDetailType, setHkDetailType] = useState('termine'); // 'termine' or 'planned'

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await invoicesApi.getAllInvoices();
      setInvoices(res.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch monthly CA summary per FDM
  const fetchMonthlySummary = useCallback(async () => {
    try {
      const now = new Date();
      const res = await paymentsApi.getMonthlySummary(now.getMonth() + 1, now.getFullYear());
      setMonthlySummary(res.data.summary || []);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // Fetch forecast summary (planned but not completed tasks)
  const fetchForecastSummary = useCallback(async () => {
    try {
      const now = new Date();
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/forecast-summary?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setForecastSummary(data.summary || []);
      }
    } catch (error) {
      console.error('Error fetching forecast summary:', error);
    } finally {
      setLoadingForecast(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchMonthlySummary();
    fetchForecastSummary();
  }, [fetchInvoices, fetchMonthlySummary, fetchForecastSummary]);

  // Auto-refetch when tab regains focus OR on custom mcb-tasks-updated event
  // (e.g. after time-billing recalc triggered from another view). Ensures the
  // "CA attendu ce mois" reflects fresh task.price values immediately.
  useEffect(() => {
    const refetchAll = () => {
      fetchInvoices();
      fetchMonthlySummary();
      fetchForecastSummary();
    };
    const onFocus = () => refetchAll();
    const onVisibility = () => { if (document.visibilityState === 'visible') refetchAll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('mcb-tasks-updated', refetchAll);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('mcb-tasks-updated', refetchAll);
    };
  }, [fetchInvoices, fetchMonthlySummary, fetchForecastSummary]);

  // Fetch housekeeper payment details
  const fetchHkDetails = async (housekeeperId, statusFilter = 'termine') => {
    setLoadingHkDetails(true);
    setHkDetailType(statusFilter);
    setShowHkDetailModal(true);
    
    try {
      const now = new Date();
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/housekeeper-details/${housekeeperId}?month=${now.getMonth() + 1}&year=${now.getFullYear()}&status_filter=${statusFilter}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setHkDetails(data);
      } else {
        toast.error('Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error('Error fetching hk details:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoadingHkDetails(false);
    }
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await invoicesApi.updateStatus(invoiceId, newStatus);
      toast.success(`Statut mis à jour : ${getStatusLabel(newStatus)}`);
      fetchInvoices();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      submitted: { 
        bg: 'bg-blue-50', 
        text: 'text-blue-600', 
        dot: 'bg-blue-400',
        label: 'En attente',
        icon: <Clock className="h-4 w-4" />
      },
      processed: { 
        bg: 'bg-amber-50', 
        text: 'text-amber-600', 
        dot: 'bg-amber-400',
        label: 'En traitement',
        icon: <Eye className="h-4 w-4" />
      },
      paid: { 
        bg: 'bg-emerald-50', 
        text: 'text-emerald-600', 
        dot: 'bg-emerald-400',
        label: 'Payée',
        icon: <CheckCircle2 className="h-4 w-4" />
      },
      rejected: { 
        bg: 'bg-red-50', 
        text: 'text-red-600', 
        dot: 'bg-red-400',
        label: 'Rejetée',
        icon: <X className="h-4 w-4" />
      },
    };
    return configs[status] || configs.submitted;
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'En attente',
      processed: 'En traitement',
      paid: 'Payée',
      rejected: 'Rejetée'
    };
    return labels[status] || status;
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.month?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: invoices.length,
    pending: invoices.filter(i => i.status === 'submitted').length,
    processing: invoices.filter(i => i.status === 'processed').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.amount || 0), 0),
    pendingAmount: invoices.filter(i => i.status !== 'paid' && i.status !== 'rejected')
                          .reduce((sum, i) => sum + (i.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center md:ml-72">
          <div className="text-center">
            <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-[#1F3A8A]/20"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#1F3A8A] animate-spin"></div>
              <div className="absolute inset-3 rounded-full overflow-hidden bg-white shadow-lg">
                <img src="/logo-mcb.png" alt="MCB" className="w-full h-full object-cover" />
              </div>
            </div>
            <p className="mt-4 text-slate-500 font-medium">Chargement des factures...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Sidebar />
      
      <main className="flex-1 md:ml-72 p-4 md:p-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Paiements</h1>
              <p className="text-slate-500 mt-1">Gérez les factures reçues de votre équipe</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Receipt className="h-5 w-5 text-white" />
              </div>
            </div>
            <p className="text-slate-500 text-sm font-medium">Total factures</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
            <p className="text-slate-500 text-sm font-medium">En attente</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.pending}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
            </div>
            <p className="text-slate-500 text-sm font-medium">Payées</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.paid}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Euro className="h-5 w-5 text-white" />
              </div>
            </div>
            <p className="text-slate-500 text-sm font-medium">À payer</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.pendingAmount.toFixed(0)}€</p>
          </div>
        </div>

        {/* CA attendu par FDM ce mois */}
        {monthlySummary.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-slate-800">CA attendu en facture ce mois</h2>
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-200">
                {format(new Date(), 'MMMM yyyy', { locale: fr })}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Cliquez sur une carte pour voir le détail des prestations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {monthlySummary.map((hk) => (
                <div 
                  key={hk.housekeeper_id} 
                  className="bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-xl p-4 border border-slate-100 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
                  data-testid={`ca-badge-${hk.housekeeper_id}`}
                  onClick={() => fetchHkDetails(hk.housekeeper_id, 'termine')}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#003366]/10 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-[#003366]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{hk.housekeeper_name}</p>
                      <p className="text-xs text-slate-500">{hk.task_count} prestation{hk.task_count > 1 ? 's' : ''}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">CA à facturer</span>
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 py-1.5 rounded-full shadow-lg shadow-blue-500/30 flex items-center gap-1.5">
                      <Euro className="h-3.5 w-3.5" />
                      <span className="font-bold">{hk.total_amount?.toFixed(0)}€</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingSummary && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-slate-500">Chargement du CA par FDM...</span>
            </div>
          </div>
        )}

        {/* CA Prévisionnel par FDM (missions planifiées non terminées) */}
        {forecastSummary.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <h2 className="text-lg font-semibold text-slate-800">CA Prévisionnel par FDM</h2>
              </div>
              <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50">
                Missions en cours
              </Badge>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Cliquez sur une carte pour voir le détail des missions planifiées
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {forecastSummary.map((hk) => (
                <div 
                  key={hk.housekeeper_id} 
                  className="bg-gradient-to-br from-orange-50 to-amber-50/50 rounded-xl p-4 border border-orange-100 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all"
                  data-testid={`forecast-badge-${hk.housekeeper_id}`}
                  onClick={() => fetchHkDetails(hk.housekeeper_id, 'planned')}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{hk.housekeeper_name}</p>
                      <p className="text-xs text-slate-500">{hk.task_count} mission{hk.task_count > 1 ? 's' : ''} à venir</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-orange-400" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">CA prévisionnel</span>
                    <div className="bg-gradient-to-r from-orange-400 to-amber-500 text-white px-3 py-1.5 rounded-full shadow-lg shadow-orange-500/30 flex items-center gap-1.5">
                      <Euro className="h-3.5 w-3.5" />
                      <span className="font-bold">{hk.total_amount?.toFixed(0)}€</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Total prévisionnel */}
            <div className="mt-4 pt-4 border-t border-orange-100 flex justify-end">
              <div className="text-right">
                <span className="text-sm text-slate-500">Total prévisionnel : </span>
                <span className="text-xl font-bold text-orange-600">
                  {forecastTotal.toFixed(0)}€
                </span>
              </div>
            </div>
          </div>
        )}

        {loadingForecast && !loadingSummary && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100 mb-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
              <span className="text-slate-500">Chargement du CA prévisionnel...</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou période..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-slate-50 border-0 rounded-xl"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 bg-slate-50 border-0 rounded-xl">
                <Filter className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="submitted">En attente</SelectItem>
                <SelectItem value="processed">En traitement</SelectItem>
                <SelectItem value="paid">Payées</SelectItem>
                <SelectItem value="rejected">Rejetées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-400" />
              Factures reçues
              <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                {filteredInvoices.length}
              </span>
            </h2>
          </div>

          {filteredInvoices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Receipt className="h-8 w-8 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium">Aucune facture</p>
              <p className="text-slate-400 text-sm mt-1">Les factures de votre équipe apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Prestataire</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Période</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date envoi</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInvoices.map((invoice) => {
                    const config = getStatusConfig(invoice.status);
                    return (
                      <tr 
                        key={invoice.id} 
                        className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowDetailDialog(true);
                        }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-medium text-sm">
                              {invoice.user_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{invoice.user_name}</p>
                              <p className="text-xs text-slate-400">{invoice.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-slate-700">{invoice.month}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-emerald-600">{invoice.amount?.toFixed(2)}€</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-500 text-sm">
                            {invoice.submitted_at ? format(new Date(invoice.submitted_at), 'd MMM yyyy', { locale: fr }) : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${config.bg}`}>
                            <div className={`w-2 h-2 rounded-full ${config.dot}`}></div>
                            <span className={`text-xs font-medium ${config.text}`}>{config.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-500 hover:text-blue-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInvoice(invoice);
                              setShowDetailDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-600" />
              Détail de la facture
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="py-4 space-y-4">
              {/* Prestataire */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                  {selectedInvoice.user_name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-lg">{selectedInvoice.user_name}</p>
                  <p className="text-slate-500 text-sm">{selectedInvoice.user_email}</p>
                  <p className="text-xs text-slate-400 capitalize">{selectedInvoice.user_role}</p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-500 font-medium mb-1">Période</p>
                  <p className="font-semibold text-slate-800">{selectedInvoice.month}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Montant</p>
                  <p className="font-bold text-emerald-700 text-xl">{selectedInvoice.amount?.toFixed(2)}€</p>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-xs text-amber-600 font-medium mb-1">Notes</p>
                  <p className="text-amber-800 text-sm">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Dates */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Envoyée le</span>
                  <span className="font-medium text-slate-700">
                    {selectedInvoice.submitted_at ? format(new Date(selectedInvoice.submitted_at), 'd MMMM yyyy à HH:mm', { locale: fr }) : '-'}
                  </span>
                </div>
                {selectedInvoice.processed_at && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Traitée le</span>
                    <span className="font-medium text-slate-700">
                      {format(new Date(selectedInvoice.processed_at), 'd MMMM yyyy', { locale: fr })}
                    </span>
                  </div>
                )}
              </div>

              {/* Current Status */}
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-xs text-blue-600 font-medium mb-2">Statut actuel</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusConfig(selectedInvoice.status).bg}`}>
                  {getStatusConfig(selectedInvoice.status).icon}
                  <span className={`font-semibold ${getStatusConfig(selectedInvoice.status).text}`}>
                    {getStatusConfig(selectedInvoice.status).label}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'rejected' && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500 mb-3">Changer le statut :</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedInvoice.status === 'submitted' && (
                      <Button
                        variant="outline"
                        onClick={() => handleStatusUpdate(selectedInvoice.id, 'processed')}
                        disabled={updatingStatus}
                        className="flex-1"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Marquer en traitement
                      </Button>
                    )}
                    <Button
                      onClick={() => handleStatusUpdate(selectedInvoice.id, 'paid')}
                      disabled={updatingStatus}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {updatingStatus ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      Marquer payée
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleStatusUpdate(selectedInvoice.id, 'rejected')}
                      disabled={updatingStatus}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Rejeter
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Housekeeper Details Modal */}
      <Dialog open={showHkDetailModal} onOpenChange={setShowHkDetailModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowHkDetailModal(false)}
                className="-ml-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <span className="text-lg">
                  Détail {hkDetailType === 'termine' ? 'des prestations terminées' : 'des missions planifiées'}
                </span>
                {hkDetails && (
                  <p className="text-sm font-normal text-slate-500 mt-1">
                    {hkDetails.housekeeper_name} - {format(new Date(hkDetails.year, hkDetails.month - 1), 'MMMM yyyy', { locale: fr })}
                  </p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingHkDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : hkDetails ? (
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              {/* Summary header */}
              <div className={`rounded-xl p-4 mb-4 ${hkDetailType === 'termine' ? 'bg-blue-50 border border-blue-100' : 'bg-orange-50 border border-orange-100'}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{hkDetails.summary.total_tasks}</p>
                    <p className="text-xs text-slate-500">{hkDetailType === 'termine' ? 'Terminées' : 'Planifiées'}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{hkDetails.summary.solo_tasks}</p>
                    <p className="text-xs text-slate-500">En solo (100%)</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{hkDetails.summary.shared_tasks}</p>
                    <p className="text-xs text-slate-500">Partagées (50/50)</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${hkDetailType === 'termine' ? 'text-blue-600' : 'text-orange-600'}`}>
                      {hkDetails.summary.total_share.toFixed(0)}€
                    </p>
                    <p className="text-xs text-slate-500">Total à facturer</p>
                  </div>
                </div>
              </div>

              {/* Tasks table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Date</th>
                      <th className="text-left p-3 font-medium text-slate-600">Logement</th>
                      <th className="text-center p-3 font-medium text-slate-600">Répartition</th>
                      <th className="text-right p-3 font-medium text-slate-600">Prix total</th>
                      <th className="text-right p-3 font-medium text-slate-600">Part FDM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {hkDetails.tasks.map((task, idx) => (
                      <tr key={task.task_id || idx} className="hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span>{format(new Date(task.date), 'd MMM', { locale: fr })}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-slate-400" />
                            <span className="font-medium truncate max-w-[200px]">{task.property_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {task.is_shared ? (
                            <div className="flex flex-col items-center">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                <Users className="h-3 w-3" />
                                {task.share_label}
                              </span>
                              {task.other_housekeepers.length > 0 && (
                                <span className="text-xs text-slate-500 mt-1">
                                  avec {task.other_housekeepers.join(', ')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                              <User className="h-3 w-3" />
                              100%
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right text-slate-500">
                          {task.full_price.toFixed(0)}€
                        </td>
                        <td className="p-3 text-right">
                          <span className={`font-bold ${task.is_shared ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {task.share.toFixed(0)}€
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2">
                    <tr>
                      <td colSpan="3" className="p-3 font-semibold text-slate-700">
                        TOTAL À FACTURER
                      </td>
                      <td className="p-3 text-right text-slate-500">
                        {hkDetails.summary.total_full_price.toFixed(0)}€
                      </td>
                      <td className="p-3 text-right">
                        <span className={`text-lg font-bold ${hkDetailType === 'termine' ? 'text-blue-600' : 'text-orange-600'}`}>
                          {hkDetails.summary.total_share.toFixed(0)}€
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Legend */}
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
                <p className="font-medium mb-1">Légende :</p>
                <div className="flex flex-wrap gap-4">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                    100% = Mission en solo
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                    50/50 = Mission partagée à 2
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              Aucune donnée disponible
            </div>
          )}

          <div className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowHkDetailModal(false)} className="w-full">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
